import React, { useState } from 'react'

import { useQuery } from 'react-query'

import {
  Box,
  Card,
  Divider,
  LinearProgress,
  RoundedPlainTextButton,
  SimpleTable,
  SimpleTableProps,
  Typography,
} from '@percept/mui'

import { ArrowLeft, ArrowRight } from '@percept/mui/icons'

import { useCardLoadingStyles } from '@percept/app-components'

import { InfinitePaginationWrapper } from '@percept/types'

import { capitalize } from 'lodash-es'


export type MediaAcademyAdminTableProps<T extends object> = (
  Omit<
    SimpleTableProps<T>,
    | 'rows'
    | 'grouped'
    | 'expandedByDefault'
    | 'getRowGroup'
    | 'onRowExpand'
    | 'onRowCollapse'
    | 'groupColor'
  >
  & {
    name: string
    queryKey: string
    queryFn: (page: number) => Promise<InfinitePaginationWrapper<T>>
    secondaryTitleContent?: React.ReactNode
  }
)


export function MediaAcademyAdminTable<T extends object>({
  name,
  queryKey,
  queryFn,
  secondaryTitleContent,
  ...props
}: MediaAcademyAdminTableProps<T>): JSX.Element {

  const [page, setPage] = useState(1)

  const { data, isPreviousData, isRefetching, isFetching } = useQuery({
    queryKey: [queryKey, 'admin', page],
    queryFn: () => queryFn(page),
    keepPreviousData: true,
    staleTime: 1000 * 60 * 2,
  })

  const classes = useCardLoadingStyles()

  const rows = data && data.items

  return (
    <Card
      className={classes.card}>
      { (isFetching || isRefetching) && (
        <LinearProgress className={classes.refetchIndicator} variant='indeterminate' />
      )}
      <Box p={2} display='flex' alignItems='center' justifyContent='space-between'>
        <Typography variant='h4'>{ capitalize(name) }</Typography>
        { secondaryTitleContent }
      </Box>
      <Divider />
      <SimpleTable
        stickyHeader
        pinFirstColumn
        rows={rows || []}
        {...props} />
      { rows && !rows.length && (
        <Box
          p={6}
          fontSize={18}
          display='flex'
          justifyContent='center'
          color='text.secondary'>
          No {name} found
        </Box>
      )}
      { data && (
        <>
          <Divider />
          <Box display='flex' alignItems='center' justifyContent='flex-end' p={2}>
            <RoundedPlainTextButton
              variant='contained'
              size='small'
              startIcon={<ArrowLeft />}
              disabled={isPreviousData || !data.previousPage}
              onClick={(): void => setPage(data.previousPage || page)}>
              Previous
            </RoundedPlainTextButton>
            <Box mx={2}>
              <Typography variant='h5'>Page {page}</Typography>
            </Box>
            <RoundedPlainTextButton
              variant='contained'
              size='small'
              endIcon={<ArrowRight />}
              disabled={isPreviousData || !data.nextPage}
              onClick={(): void => setPage(data.nextPage || page)}>
              Next
            </RoundedPlainTextButton>
          </Box>
        </>
      )}
    </Card>
  )
}
